/**
 * TopBar Component
 * 
 * App header with back button, centered title, and responsive navigation.
 * Mobile: Shows back button and optional menu button
 * Desktop: Shows hamburger menu with main navigation items
 * 
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

import { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, MoreVertical, Menu, X } from 'lucide-react';
import MobileContextMenu, { type MobileMenuConfig } from './MobileContextMenu';
import { MAIN_NAV_ITEMS, filterVisibleItems } from '../config/navigationConfig';
import { getZIndexClass } from '../config/zIndex';

interface TopBarProps {
  title: string;
  onBack?: () => void;
  onMenu?: () => void; // Legacy support - will be deprecated
  mobileMenu?: MobileMenuConfig; // New mobile menu system
}

export default function TopBar({ title, onBack, onMenu, mobileMenu }: TopBarProps) {
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDesktopMenuOpen(false);
      }
    }

    if (isDesktopMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDesktopMenuOpen]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsDesktopMenuOpen(false);
      }
    }

    if (isDesktopMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isDesktopMenuOpen]);

  const toggleDesktopMenu = () => {
    setIsDesktopMenuOpen(!isDesktopMenuOpen);
  };

  const closeDesktopMenu = () => {
    setIsDesktopMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Handle mobile menu action - use new system if available, fallback to legacy
  const handleMobileMenuAction = () => {
    if (mobileMenu) {
      toggleMobileMenu();
    } else if (onMenu) {
      onMenu();
    }
  };

  const visibleNavItems = filterVisibleItems(MAIN_NAV_ITEMS);

  return (
    <header className={`w-full bg-oro-surface border-b border-oro-border ${getZIndexClass('topBar')}`}>
      {/* Responsive inner container matching main content width */}
      <div className="w-full max-w-md md:max-w-4xl lg:max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between relative">
        {/* Left - Mobile Back Button / Desktop Nav */}
        <div className="flex items-center">
          {/* Mobile Back Button */}
          {onBack ? (
            <button
              onClick={onBack}
              className="p-2 -ml-2 hover:opacity-80 transition-opacity min-w-11 min-h-11 md:hidden"
              aria-label="Back"
            >
              <ChevronLeft className="w-6 h-6 text-accent" />
            </button>
          ) : (
            <div className="min-w-11 md:hidden" />
          )}
          
          {/* Desktop: Show main nav items directly */}
          <nav className="hidden md:flex items-center gap-6">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `text-sm transition-colors font-medium whitespace-nowrap ${
                    isActive ? 'text-oro-gold' : 'text-text-secondary hover:text-text-primary'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Center - Title */}
        <h1 className="absolute left-1/2 -translate-x-1/2 text-text-primary font-bold md:text-lg whitespace-nowrap">
          {title}
        </h1>

      {/* Right - Navigation & Menus */}
      <div className="flex items-center">
        {/* Desktop More Menu (Optional) */}
        <div className="hidden md:block relative">
          <button
            ref={buttonRef}
            onClick={toggleDesktopMenu}
            className="p-2 -mr-2 hover:opacity-80 transition-opacity text-text-primary"
            aria-label="More options"
            aria-expanded={isDesktopMenuOpen}
          >
            {isDesktopMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <MoreVertical className="w-6 h-6" />
            )}
          </button>

          {/* Desktop More Dropdown */}
          {isDesktopMenuOpen && (
            <div
              ref={menuRef}
              className={`absolute top-full right-0 mt-2 w-64 bg-oro-surface border border-oro-border rounded-lg shadow-lg ${getZIndexClass('dropdown')}`}
            >
              <nav className="py-2" role="menu">
                <button
                  className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-oro-surface-alt transition-colors text-text-primary"
                  role="menuitem"
                  onClick={closeDesktopMenu}
                >
                  <span className="font-medium">Settings</span>
                </button>
                <button
                  className="flex items-center gap-3 px-4 py-3 w-full text-left hover:bg-oro-surface-alt transition-colors text-text-primary"
                  role="menuitem"
                  onClick={closeDesktopMenu}
                >
                  <span className="font-medium">Help & Legal</span>
                </button>
              </nav>
            </div>
          )}
        </div>
        
        {/* Mobile Menu */}
        <div className="md:hidden relative">
          {(mobileMenu || onMenu) && (
            <>
              <button
                onClick={handleMobileMenuAction}
                className="p-2 -mr-2 hover:opacity-80 transition-opacity text-text-muted min-w-11 min-h-11"
                aria-label={mobileMenu ? "Navigation menu" : "More options"}
                aria-expanded={mobileMenu ? isMobileMenuOpen : undefined}
              >
                {mobileMenu ? (
                  isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />
                ) : (
                  <MoreVertical className="w-6 h-6" />
                )}
              </button>
              
              {/* Mobile Context Menu */}
              {mobileMenu && (
                <MobileContextMenu
                  config={mobileMenu}
                  isOpen={isMobileMenuOpen}
                  onClose={closeMobileMenu}
                />
              )}
            </>
          )}
          {!mobileMenu && !onMenu && <div className="min-w-11" />}
        </div>
      </div>
      </div> {/* Close responsive inner container */}
    </header>
  );
}
